import React, { useState, useEffect } from 'react';
import { Calendar, Wrench, Check, CheckCircle, AlertCircle, DollarSign, Camera } from 'lucide-react';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/helpers';
import { getQuoteAge } from '../../pages/Jobs';
import { useBusinessContext } from '../../contexts/BusinessContext';

// Import professional styling (optional - can be inline if you prefer)
// import { jobStyles, getPersonalJobCardClass, PersonalServiceIndicator, PersonalAmountDisplay } from './jobStyles';

const JobCard = ({
  job,
  onEdit,
  onDelete,
  onSendPrepEmail,
  onSendQuoteEmail,
  onSendQuoteSms,
  onComplete,
  onCardClick,
  sendingEmail,
  sendingQuoteEmail,
  sendingQuoteSms,
  smsSettings,
}) => {
  const { user } = useBusinessContext();
  const isOwner = user?.role === 'Owner';
  
  // Check if this is a Personal/Free job
  const isPersonalJob = job.transactionType === 'Personal';
  
  // Photo summary not loaded on card - loaded in drawer on demand
  const photoSummary = null;

  // Format full address
  const formatAddress = (job) => {
    const parts = [job.address, job.city, job.state, job.zipCode].filter(Boolean);
    return parts.join(', ');
  };

  // Get service names from job services
  const getServiceNames = (job) => {
    if (job.jobServices && job.jobServices.length > 0) {
      return job.jobServices
        .map(js => js.serviceType?.name)
        .filter(Boolean)
        .join(', ');
    }
    return null;
  };

  // Check if prep email can be sent
  const canSendPrepEmail = (job) => {
    return job.status === 'Scheduled' && 
           !job.prepEmailSent && 
           job.scheduledDate && 
           job.customer?.email;
  };

  // Check if quote email can be sent (Owner only)
  const canSendQuoteEmail = (job) => {
    const hasAmount = job.quoteAmount > 0;
    const hasServices = job.jobServices && job.jobServices.length > 0;
    const hasEmail = job.customer?.email;
    
    return isOwner &&
           job.status === 'Quoted' && 
           !job.quoteEmailSent && 
           hasEmail &&
           hasAmount &&
           hasServices;
  };

  // Check why quote email can't be sent (for showing helpful message)
  const getQuoteEmailBlockReason = (job) => {
    if (!isOwner) return null;
    if (job.status !== 'Quoted') return null;
    if (job.quoteEmailSent) return null;
    
    const issues = [];
    if (!job.customer?.email) issues.push('no email');
    if (!job.quoteAmount || job.quoteAmount <= 0) issues.push('no quote amount');
    if (!job.jobServices || job.jobServices.length === 0) issues.push('no service selected');
    
    return issues.length > 0 ? issues.join(', ') : null;
  };

  // Check if job can be completed (Scheduled or In Progress)
  const canComplete = (job) => {
    return job.status === 'Scheduled' || job.status === 'In Progress';
  };

  // Handle card click (open drawer)
  const handleCardClick = (e) => {
    // Don't trigger if clicking on buttons or interactive elements
    if (e.target.closest('button') || e.target.closest('a')) {
      return;
    }
    onCardClick?.(job);
  };

  // Professional status badge styling for Personal jobs
  const getPersonalStatusColor = (job) => {
    if (isPersonalJob && job.status === 'Completed') {
      return 'bg-slate-600 text-white border-slate-700';
    }
    return getStatusColor(job.status);
  };

  // Professional status text
  const getPersonalStatusText = (job) => {
    if (isPersonalJob && job.status === 'Completed') {
      return 'Completed (Personal)';
    }
    return job.status;
  };

  const serviceNames = getServiceNames(job);
  const showCompleteButton = canComplete(job);
  const quoteBlockReason = getQuoteEmailBlockReason(job);

  // PROFESSIONAL: Subtle dark styling for Personal jobs
  const cardClassName = `card p-0 overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${
    isPersonalJob 
      ? 'bg-slate-50/50 border-gray-200 border-l-4 border-l-slate-700' 
      : ''
  }`;

  return (
    <div className={cardClassName} onClick={handleCardClick}>
      {/* Header */}
      <div className="flex items-start justify-between p-4 pb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">
            {job.customer?.name || 'Unknown Customer'}
          </h3>
          <p className="text-sm text-gray-500 truncate">{formatAddress(job)}</p>
          
          {/* PROFESSIONAL: Personal/Free indicator with subtle styling */}
          {isPersonalJob && (
            <div className="flex items-center gap-2 mt-1">
              <svg 
                className="w-3.5 h-3.5 text-slate-600" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path 
                  fillRule="evenodd" 
                  d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" 
                  clipRule="evenodd" 
                />
              </svg>
              <span className="text-xs font-medium text-slate-700">
                Personal/Family Service
              </span>
            </div>
          )}
        </div>
        
        {/* PROFESSIONAL: Status badge with dark styling for Personal */}
        <span className={`badge ${getPersonalStatusColor(job)} ml-2 flex-shrink-0`}>
          {getPersonalStatusText(job)}
        </span>
      </div>

      {/* Details */}
      <div className="px-4 pb-3 space-y-2">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          {job.scheduledDate && (
            <div className="flex items-center gap-1.5 text-gray-600">
              <Calendar size={14} />
              {formatDate(job.scheduledDate)}
            </div>
          )}
          {serviceNames && (
            <div className="flex items-center gap-1.5 text-gray-600">
              <Wrench size={14} />
              <span className="truncate max-w-[200px]">{serviceNames}</span>
              {job.jobServices?.length > 1 && (
                <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                  +{job.jobServices.length - 1}
                </span>
              )}
            </div>
          )}
          
          {/* Photo Count */}
          {photoSummary && photoSummary.totalCount > 0 && (
            <div className="flex items-center gap-1.5 text-gray-600">
              <Camera size={14} />
              <span>{photoSummary.totalCount} photo{photoSummary.totalCount !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
        
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {/* PROFESSIONAL: Quote Amount with subtle Personal styling */}
          {job.status === 'Quoted' && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">
              {getQuoteAge(job.createdAt)}
            </span>
          )}
          {isOwner && job.quoteAmount > 0 && (
            <>
              {isPersonalJob ? (
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 line-through text-sm">
                    Quote: {formatCurrency(job.quoteAmount)}
                  </span>
                  <span className="font-semibold text-slate-700 px-2 py-0.5 bg-slate-200 rounded-full text-xs border border-slate-300">
                    No Charge
                  </span>
                </div>
              ) : (
                <span className="font-semibold text-primary-600">
                  Quote: {formatCurrency(job.quoteAmount)}
                </span>
              )}
            </>
          )}
          
          {/* Warning: Missing service or amount for Quoted jobs */}
          {isOwner && job.status === 'Quoted' && quoteBlockReason && (
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full flex items-center gap-1">
              <AlertCircle size={12} />
              Missing: {quoteBlockReason}
            </span>
          )}
          
          {job.isInvoiced && (
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              isPersonalJob 
                ? 'bg-slate-200 text-slate-700 border border-slate-300' 
                : 'bg-green-100 text-green-700'
            }`}>
              {isPersonalJob ? 'Recorded ($0)' : 'Invoiced'}
            </span>
          )}
          
          {/* Contract Billing indicator */}
          {job.customer?.billingType && job.customer.billingType !== 'PerJob' && (
            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
              <DollarSign size={12} />
              {job.customer.billingType}
            </span>
          )}
          
          {/* OWNER ONLY: Quote Sent indicator */}
{isOwner && job.quoteEmailSent && (
  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
    <Check size={12} />
    Quote Sent
  </span>
)}
{isOwner && job.quoteSmsSent && (
  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
    <Check size={12} />
    Quote Texted
  </span>
)}
          
          {job.prepEmailSent && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1">
              <Check size={12} />
              Prep Email Sent
            </span>
          )}
        </div>

        {/* Photo Thumbnails Preview */}
        {photoSummary && photoSummary.totalCount > 0 && photoSummary.previewUrls && photoSummary.previewUrls.length > 0 && (
          <div className="flex items-center gap-2 pt-1">
            <div className="flex -space-x-2">
              {photoSummary.previewUrls.slice(0, 3).map((url, idx) => (
                <img
                  key={idx}
                  src={url}
                  alt=""
                  className="w-10 h-10 rounded-lg object-cover border-2 border-white shadow-sm"
                  style={{ zIndex: 3 - idx }}
                />
              ))}
              {photoSummary.totalCount > 3 && (
                <div 
                  className="w-10 h-10 rounded-lg bg-gray-200 border-2 border-white shadow-sm flex items-center justify-center"
                  style={{ zIndex: 0 }}
                >
                  <span className="text-xs font-medium text-gray-600">
                    +{photoSummary.totalCount - 3}
                  </span>
                </div>
              )}
            </div>
            {/* Photo type indicators */}
            <div className="flex items-center gap-1">
              {photoSummary.beforeCount > 0 && (
                <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                  📷 {photoSummary.beforeCount}
                </span>
              )}
              {photoSummary.afterCount > 0 && (
                <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                  ✅ {photoSummary.afterCount}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="border-t border-gray-100 p-3 bg-gray-50">
        {/* Primary Actions Row */}
        <div className="flex items-center gap-2">
          <span className={`px-2.5 py-1.5 rounded-lg text-xs font-medium ${getPersonalStatusColor(job)}`}>
            {getPersonalStatusText(job)}
          </span>

          {/* OWNER ONLY: Quote Email Button */}
{canSendQuoteEmail(job) && (
  <button
    onClick={(e) => {
      e.stopPropagation();
      onSendQuoteEmail(job.id);
    }}
    disabled={sendingQuoteEmail === job.id}
    className="btn bg-purple-600 hover:bg-purple-700 text-white text-xs py-1.5 px-2.5"
  >
    {sendingQuoteEmail === job.id ? 'Sending...' : 'Email Quote'}
  </button>
)}

{/* OWNER ONLY: Text Quote Button */}
{isOwner && job.status === 'Quoted' && !job.quoteSmsSent && job.customer?.phone && job.quoteAmount > 0 && job.jobServices?.length > 0 && smsSettings?.smsQuoteTextingEnabled && smsSettings?.smsNotificationsEnabled && (
  <button
    onClick={(e) => {
      e.stopPropagation();
      onSendQuoteSms(job.id);
    }}
    disabled={sendingQuoteSms === job.id}
    className="btn bg-purple-700 hover:bg-purple-800 text-white text-xs py-1.5 px-2.5 disabled:opacity-50"
  >
    {sendingQuoteSms === job.id ? 'Sending...' : 'Text Quote'}
  </button>
)}

          {/* Prep Email Button */}
          {canSendPrepEmail(job) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSendPrepEmail(job.id);
              }}
              disabled={sendingEmail === job.id}
              className="btn btn-primary text-xs py-1.5 px-2.5"
            >
              {sendingEmail === job.id ? 'Sending...' : 'Prep Email'}
            </button>
          )}

          {/* Complete Button */}
          {showCompleteButton && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onComplete(job);
              }}
              className="btn bg-green-600 hover:green-700 text-white text-xs py-1.5 px-2.5 flex items-center gap-1"
            >
              <CheckCircle size={12} />
              Complete
            </button>
          )}
        </div>

        {/* Edit/Delete Row */}
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(job);
            }}
            className="flex-1 px-3 py-2 text-sm text-primary-600 font-medium hover:bg-primary-50 rounded-lg transition-colors border border-primary-200"
          >
            {isOwner ? 'Edit' : 'Reschedule'}
          </button>
          {/* OWNER ONLY: Delete Button */}
          {isOwner && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(job.id);
              }}
              className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobCard;